<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CaptainScenarios from 'dashboard/api/captain/scenarios';
import { useDictation } from 'dashboard/composables/useDictation';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['generated']);

const { t } = useI18n();

const dialogRef = ref(null);
const isGenerating = ref(false);
const preview = ref(null);
const filters = ref({
  inbox_id: '',
  conversation_status: 'resolved',
  since: '',
  until: '',
  contact_type: [],
});

// Kept apart from the filters: it does not change how many conversations match, so typing
// in it must not re-query the preview on every keystroke.
const briefing = ref('');

const {
  isSupported: canDictate,
  isListening,
  toggle: toggleDictation,
  stop: stopDictation,
} = useDictation({ target: briefing });

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxOptions = computed(() => [
  {
    value: '',
    label: t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.ALL_INBOXES'),
  },
  ...(inboxes.value ?? []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  })),
]);

const statusOptions = computed(() => [
  {
    value: 'resolved',
    label: t(
      'CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.STATUS_OPTIONS.RESOLVED'
    ),
  },
  {
    value: 'open',
    label: t(
      'CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.STATUS_OPTIONS.OPEN'
    ),
  },
  {
    value: 'pending',
    label: t(
      'CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.STATUS_OPTIONS.PENDING'
    ),
  },
]);

const eligibleCount = computed(() => preview.value?.eligible_count ?? 0);
const canGenerate = computed(() => preview.value?.available === true);

// Offered from the account's own definition of the attribute, so an account that renamed or
// trimmed the list is not shown types that would match nothing.
const contactTypeOptions = computed(() => preview.value?.contact_types ?? []);

const toggleContactType = value => {
  const selected = filters.value.contact_type;
  filters.value.contact_type = selected.includes(value)
    ? selected.filter(type => type !== value)
    : [...selected, value];
};

// Why the analysis cannot run, in the user's words. Each reason needs a different fix, so
// a single "not available" message would leave them stuck.
const blockedMessage = computed(() => {
  const reason = preview.value?.reason;
  if (!reason) return '';

  if (reason === 'nothing_to_analyse') {
    return t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BLOCKED.NOTHING');
  }
  if (reason === 'not_enough_conversations') {
    return t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BLOCKED.NOT_ENOUGH', {
      count: eligibleCount.value,
      minimum: preview.value.minimum,
    });
  }
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BLOCKED.ALREADY_GENERATED');
});

// Missing tools no longer stop the analysis: a rubro may suggest tools this client will
// never set up. But whoever launches it should know the scenarios cannot reference them.
const pendingToolsWarning = computed(() => {
  const tools = preview.value?.pending_tools ?? [];
  if (!tools.length) return '';

  return t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.WARNING.PENDING_TOOLS', {
    tools: tools.join(', '),
  });
});

const fetchPreview = async () => {
  try {
    const { data } = await CaptainScenarios.generationPreview({
      assistantId: props.assistantId,
      ...filters.value,
    });
    preview.value = data;
  } catch (error) {
    preview.value = null;
  }
};

watch(filters, fetchPreview, { deep: true });

const open = async () => {
  await fetchPreview();
  dialogRef.value?.open();
};

const handleConfirm = async () => {
  isGenerating.value = true;
  try {
    const { data } = await CaptainScenarios.generate({
      assistantId: props.assistantId,
      ...filters.value,
      briefing: briefing.value,
    });
    useAlert(
      t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.SUCCESS_MESSAGE', {
        count: data.conversation_count,
      })
    );
    emit('generated');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.ERROR_MESSAGE')
    );
  } finally {
    isGenerating.value = false;
  }
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.TITLE')"
    :description="t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.DESCRIPTION')"
    :confirm-button-label="t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.CONFIRM')"
    :is-loading="isGenerating"
    :disable-confirm-button="isGenerating || !canGenerate"
    @confirm="handleConfirm"
    @close="stopDictation"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.INBOX') }}
        </label>
        <ComboBox v-model="filters.inbox_id" :options="inboxOptions" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.STATUS') }}
        </label>
        <ComboBox
          v-model="filters.conversation_status"
          :options="statusOptions"
        />
      </div>

      <div v-if="contactTypeOptions.length" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.CONTACT_TYPE') }}
        </label>
        <div class="flex flex-wrap gap-x-4 gap-y-2">
          <label
            v-for="type in contactTypeOptions"
            :key="type"
            class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11"
          >
            <Checkbox
              :model-value="filters.contact_type.includes(type)"
              @update:model-value="toggleContactType(type)"
            />
            {{ type }}
          </label>
        </div>
        <span class="text-xs text-n-slate-10">
          {{
            t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.CONTACT_TYPE_HINT')
          }}
        </span>
      </div>

      <div class="flex gap-3">
        <Input
          v-model="filters.since"
          type="date"
          class="flex-1"
          :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.SINCE')"
        />
        <Input
          v-model="filters.until"
          type="date"
          class="flex-1"
          :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.FILTERS.UNTIL')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BRIEFING.LABEL') }}
          </label>
          <Button
            v-if="canDictate"
            type="button"
            :label="
              isListening
                ? t(
                    'CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BRIEFING.DICTATE_STOP'
                  )
                : t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BRIEFING.DICTATE')
            "
            :icon="isListening ? 'i-lucide-square' : 'i-lucide-mic'"
            :color="isListening ? 'ruby' : 'slate'"
            :class="{ 'animate-pulse': isListening }"
            ghost
            xs
            @click="toggleDictation"
          />
        </div>
        <TextArea
          v-model="briefing"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BRIEFING.PLACEHOLDER')
          "
          :max-length="4000"
          auto-height
          min-height="6rem"
          max-height="14rem"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BRIEFING.HINT') }}
        </span>
      </div>

      <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-2">
        <span class="text-sm text-n-slate-12">
          {{
            t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.MATCHING', {
              count: eligibleCount,
            })
          }}
        </span>
        <span v-if="blockedMessage" class="text-sm text-n-ruby-11">
          {{ blockedMessage }}
        </span>
        <span v-if="pendingToolsWarning" class="text-sm text-n-amber-11">
          {{ pendingToolsWarning }}
        </span>
        <span v-if="!blockedMessage" class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.ONE_SHOT_WARNING') }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
